// LabsAdminTab - Admin interface for managing interactive labs
// Inspired by labs-devops application, integrated into DevOpsLearn
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import api, { categoryAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FlaskConical, Plus, Pencil, Trash2, Search, Download,
  Terminal, BookOpen, Trophy, BarChart3, ChevronUp, ChevronDown, ChevronRight,
  HelpCircle, Loader2, Users, Clock, CheckCircle, X, Eye, Sparkles, Wand2,
  Server, Container
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "docker", label: "Docker", color: "bg-blue-500/20 text-blue-400" },
  { value: "kubernetes", label: "Kubernetes", color: "bg-purple-500/20 text-purple-400" },
  { value: "linux", label: "Linux", color: "bg-orange-500/20 text-orange-400" },
  { value: "ansible", label: "Ansible", color: "bg-red-500/20 text-red-400" },
  { value: "terraform", label: "Terraform", color: "bg-indigo-500/20 text-indigo-400" },
  { value: "devops", label: "DevOps", color: "bg-cyan-500/20 text-cyan-400" },
  { value: "cloud", label: "Cloud", color: "bg-emerald-500/20 text-emerald-400" },
  { value: "networking", label: "Réseau", color: "bg-amber-500/20 text-amber-400" },
  { value: "security", label: "Sécurité", color: "bg-rose-500/20 text-rose-400" },
  { value: "other", label: "Autre", color: "bg-gray-500/20 text-gray-400" },
];

const DIFFICULTIES = [
  { value: "beginner", label: "Débutant", color: "bg-green-500/20 text-green-400" },
  { value: "intermediate", label: "Intermédiaire", color: "bg-yellow-500/20 text-yellow-400" },
  { value: "advanced", label: "Avancé", color: "bg-red-500/20 text-red-400" },
];

const STEP_TYPES = [
  { value: "instruction", label: "📖 Instruction", desc: "Contenu théorique" },
  { value: "task", label: "⚡ Tâche", desc: "Commande à exécuter" },
  { value: "quiz", label: "❓ Quiz", desc: "Question à répondre" },
];

// Step Editor Component
function StepEditor({ steps, setSteps, labForm }) {
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiConfig, setAiConfig] = useState({
    num_steps: 5,
    custom_instructions: "",
    replace_existing: true,
  });

  const generateWithAI = async () => {
    setAiGenerating(true);
    try {
      const payload = {
        title: labForm.title || "Lab DevOps",
        description: labForm.description || "",
        category: labForm.category || "docker",
        difficulty: labForm.difficulty || "beginner",
        num_steps: aiConfig.num_steps,
        custom_instructions: aiConfig.custom_instructions,
        workspace_id: labForm.workspace_id || null,
      };
      const res = await api.post("/labs/admin/generate-steps", payload);
      const generated = res.data.steps || [];
      if (generated.length === 0) {
        toast.error("L'IA n'a généré aucune étape. Veuillez réessayer.");
        return;
      }
      if (aiConfig.replace_existing) {
        setSteps(generated);
      } else {
        const merged = [...steps, ...generated.map((s, i) => ({ ...s, order: steps.length + i }))];
        setSteps(merged);
      }
      toast.success(`${generated.length} étapes générées par l'IA !`);
      setAiDialogOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la génération IA";
      toast.error(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, {
      id: Date.now().toString(),
      order: steps.length,
      title: `Étape ${steps.length + 1}`,
      content: "# Nouvelle étape\n\nAjoutez votre contenu ici...",
      step_type: "instruction",
      quiz_options: [],
      quiz_correct_answer: null,
      validation_script: "",
      hint: "",
    }]);
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const removeStep = (index) => {
    if (steps.length <= 1) {
      toast.error("Le lab doit contenir au moins une étape");
      return;
    }
    const newSteps = steps.filter((_, i) => i !== index);
    newSteps.forEach((s, i) => (s.order = i));
    setSteps(newSteps);
  };

  const moveStep = (index, dir) => {
    if ((dir === -1 && index === 0) || (dir === 1 && index === steps.length - 1)) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[index + dir]] = [newSteps[index + dir], newSteps[index]];
    newSteps.forEach((s, i) => (s.order = i));
    setSteps(newSteps);
  };

  const addQuizOption = (stepIndex) => {
    const newSteps = [...steps];
    const opts = newSteps[stepIndex].quiz_options || [];
    opts.push(`Option ${opts.length + 1}`);
    newSteps[stepIndex].quiz_options = opts;
    setSteps(newSteps);
  };

  const updateQuizOption = (stepIndex, optIndex, value) => {
    const newSteps = [...steps];
    newSteps[stepIndex].quiz_options[optIndex] = value;
    setSteps(newSteps);
  };

  const removeQuizOption = (stepIndex, optIndex) => {
    const newSteps = [...steps];
    newSteps[stepIndex].quiz_options = newSteps[stepIndex].quiz_options.filter((_, i) => i !== optIndex);
    if (newSteps[stepIndex].quiz_correct_answer === optIndex) {
      newSteps[stepIndex].quiz_correct_answer = null;
    } else if (newSteps[stepIndex].quiz_correct_answer > optIndex) {
      newSteps[stepIndex].quiz_correct_answer--;
    }
    setSteps(newSteps);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Étapes ({steps.length})</h3>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAiDialogOpen(true)} size="sm" variant="outline" className="gap-1 border-violet-500/40 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300">
            <Wand2 className="h-3 w-3" /> Générer par IA
          </Button>
          <Button onClick={addStep} size="sm" variant="outline">
            <Plus className="h-3 w-3 mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      {/* AI Generation Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-1.5 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-lg">
                <Wand2 className="h-4 w-4 text-violet-400" />
              </div>
              Générer les étapes par IA
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="p-3 bg-violet-500/5 border border-violet-500/20 rounded-lg">
              <p className="text-xs text-muted-foreground">
                L'IA va générer les étapes en se basant sur le <strong>titre</strong>, la <strong>description</strong>,
                la <strong>catégorie</strong> et la <strong>difficulté</strong> du lab. Assurez-vous de les remplir dans l'onglet Détails.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Nombre d'étapes à générer</Label>
              <div className="flex items-center gap-3">
                {[3, 5, 7, 10].map(n => (
                  <Button
                    key={n}
                    size="sm"
                    variant={aiConfig.num_steps === n ? "default" : "outline"}
                    className={aiConfig.num_steps === n ? "bg-violet-600 hover:bg-violet-700" : ""}
                    onClick={() => setAiConfig({ ...aiConfig, num_steps: n })}
                  >
                    {n}
                  </Button>
                ))}
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={aiConfig.num_steps}
                  onChange={(e) => setAiConfig({ ...aiConfig, num_steps: Math.min(20, Math.max(1, parseInt(e.target.value) || 5)) })}
                  className="w-16 h-8 text-sm text-center"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Instructions personnalisées (optionnel)</Label>
              <Textarea
                value={aiConfig.custom_instructions}
                onChange={(e) => setAiConfig({ ...aiConfig, custom_instructions: e.target.value })}
                placeholder="Ex: Focus sur la sécurité des containers, inclure un exercice de Dockerfile multi-stage..."
                rows={3}
                className="text-sm"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <Label className="text-sm">Remplacer les étapes existantes</Label>
                <p className="text-[10px] text-muted-foreground">Sinon, les nouvelles étapes seront ajoutées à la fin</p>
              </div>
              <Switch
                checked={aiConfig.replace_existing}
                onCheckedChange={(v) => setAiConfig({ ...aiConfig, replace_existing: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAiDialogOpen(false)} disabled={aiGenerating}>
              Annuler
            </Button>
            <Button
              onClick={generateWithAI}
              disabled={aiGenerating || !labForm.title?.trim()}
              className="gap-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              {aiGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Générer {aiConfig.num_steps} étapes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {steps.map((step, index) => (
          <div key={step.id || index} className="border border-border rounded-lg p-3 space-y-3 bg-muted/30">
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-0.5">
                <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => moveStep(index, -1)} disabled={index === 0}>
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1}>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>
              <Badge variant="outline" className="font-mono text-xs">{index + 1}</Badge>
              <Input
                value={step.title}
                onChange={(e) => updateStep(index, "title", e.target.value)}
                className="flex-1 h-8 text-sm"
                placeholder="Titre de l'étape"
              />
              <Select value={step.step_type} onValueChange={(v) => updateStep(index, "step_type", v)}>
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STEP_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeStep(index)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>

            <Textarea
              value={step.content}
              onChange={(e) => updateStep(index, "content", e.target.value)}
              placeholder="Contenu markdown..."
              rows={3}
              className="text-xs font-mono"
            />

            {step.step_type === "quiz" && (
              <div className="space-y-2 p-2 bg-purple-500/5 rounded-md border border-purple-500/20">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-purple-400">Options du quiz</Label>
                  <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => addQuizOption(index)}>
                    <Plus className="h-3 w-3 mr-1" /> Option
                  </Button>
                </div>
                {(step.quiz_options || []).map((opt, optIndex) => (
                  <div key={optIndex} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${index}`}
                      checked={step.quiz_correct_answer === optIndex}
                      onChange={() => updateStep(index, "quiz_correct_answer", optIndex)}
                      className="h-3 w-3 accent-green-500"
                    />
                    <Input
                      value={opt}
                      onChange={(e) => updateQuizOption(index, optIndex, e.target.value)}
                      className="flex-1 h-7 text-xs"
                    />
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => removeQuizOption(index, optIndex)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <p className="text-[10px] text-muted-foreground">Sélectionnez la bonne réponse avec le radio button</p>
              </div>
            )}

            {step.step_type === "task" && (
              <div className="space-y-1">
                <Label className="text-xs">Script de validation</Label>
                <Input
                  value={step.validation_script || ""}
                  onChange={(e) => updateStep(index, "validation_script", e.target.value)}
                  placeholder="docker ps | grep nginx"
                  className="font-mono text-xs h-7"
                />
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Indice (optionnel)</Label>
              <Input
                value={step.hint || ""}
                onChange={(e) => updateStep(index, "hint", e.target.value)}
                placeholder="Indice pour aider le candidat"
                className="text-xs h-7"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LabsAdminTab() {
  const { user } = useAuth();
  const [labs, setLabs] = useState([]);
  const [stats, setStats] = useState(null);
  const [results, setResults] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [expandedResults, setExpandedResults] = useState(new Set());

  const toggleResult = (sessionId) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };
  const [courses, setCourses] = useState([]);
  const [wsCategories, setWsCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("labs");

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const [labForm, setLabForm] = useState({
    title: "", description: "", category: "docker", difficulty: "beginner",
    duration_minutes: 30, tags: "", workspace_id: "", course_id: "", category_id: "",
    terminal_environment: {},
    steps: [{ id: "1", order: 0, title: "Introduction", content: "# Bienvenue !\n\nCeci est la première étape.", step_type: "instruction", quiz_options: [], quiz_correct_answer: null, validation_script: "", hint: "" }],
  });

  const fetchData = useCallback(async () => {
    try {
      const [labsRes, statsRes, resultsRes, wsRes] = await Promise.all([
        api.get("/labs/admin/list"),
        api.get("/labs/admin/stats"),
        api.get("/labs/admin/results/list"),
        api.get("/labs/workspaces/list"),
      ]);
      setLabs(labsRes.data);
      setStats(statsRes.data);
      setResults(resultsRes.data);
      setWorkspaces(wsRes.data);
    } catch (err) {
      console.error("Failed to load labs data:", err);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadCourses = async (workspaceId) => {
    if (!workspaceId) { setCourses([]); return; }
    try {
      const res = await api.get(`/labs/courses/${workspaceId}`);
      setCourses(res.data);
    } catch { setCourses([]); }
  };

  const loadWsCategories = async (workspaceId) => {
    if (!workspaceId) { setWsCategories([]); return; }
    try {
      const res = await categoryAPI.list(workspaceId);
      setWsCategories(res.data || []);
    } catch { setWsCategories([]); }
  };

  const resetForm = () => {
    setEditingLab(null);
    setLabForm({
      title: "", description: "", category: "docker", difficulty: "beginner",
      duration_minutes: 30, tags: "", workspace_id: "", course_id: "", category_id: "",
      terminal_environment: {},
      steps: [{ id: "1", order: 0, title: "Introduction", content: "# Bienvenue !\n\nCeci est la première étape.", step_type: "instruction", quiz_options: [], quiz_correct_answer: null, validation_script: "", hint: "" }],
    });
    setCourses([]);
    setWsCategories([]);
  };

  const openEditDialog = async (lab) => {
    try {
      const res = await api.get(`/labs/admin/${lab.id}`);
      const fullLab = res.data;
      setEditingLab(lab);
      setLabForm({
        title: fullLab.title, description: fullLab.description,
        category: fullLab.category, difficulty: fullLab.difficulty,
        duration_minutes: fullLab.duration_minutes,
        tags: (fullLab.tags || []).join(", "),
        workspace_id: fullLab.workspace_id || "",
        course_id: fullLab.course_id || "",
        category_id: fullLab.category_id || "",
        terminal_environment: fullLab.terminal_environment || {},
        steps: fullLab.steps || [],
      });
      if (fullLab.workspace_id) {
        await loadCourses(fullLab.workspace_id);
        await loadWsCategories(fullLab.workspace_id);
      }
      setIsDialogOpen(true);
    } catch { toast.error("Erreur lors du chargement du lab"); }
  };

  const handleSave = async () => {
    if (!labForm.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    if (labForm.steps.length === 0) {
      toast.error("Au moins une étape est requise");
      return;
    }

    const payload = {
      ...labForm,
      tags: labForm.tags.split(",").map(t => t.trim()).filter(Boolean),
      created_by: user?.id,
    };

    try {
      if (editingLab) {
        await api.put(`/labs/admin/${editingLab.id}`, payload);
        toast.success("Lab mis à jour avec succès");
      } else {
        await api.post("/labs/admin/create", payload);
        toast.success("Lab créé avec succès");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const handleDelete = async (labId) => {
    if (!window.confirm("Supprimer ce lab et toutes ses sessions ?")) return;
    try {
      await api.delete(`/labs/admin/${labId}`);
      toast.success("Lab supprimé");
      fetchData();
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  const getCategoryBadge = (cat) => {
    const c = CATEGORIES.find(c => c.value === cat);
    return <Badge className={c?.color || "bg-gray-500/20 text-gray-400"}>{c?.label || cat}</Badge>;
  };

  const getDifficultyBadge = (diff) => {
    const d = DIFFICULTIES.find(d => d.value === diff);
    return <Badge className={d?.color || "bg-gray-500/20 text-gray-400"}>{d?.label || diff}</Badge>;
  };

  const filteredLabs = labs.filter(lab => {
    const matchSearch = lab.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        lab.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = categoryFilter === "all" || lab.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl border border-cyan-500/30">
            <FlaskConical className="h-6 w-6 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Labs Interactifs</h2>
            <p className="text-sm text-muted-foreground">Gérer les labs, étapes, et résultats</p>
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
              <Plus className="h-4 w-4" /> Créer un Lab
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-cyan-400" />
                {editingLab ? "Modifier le Lab" : "Créer un Lab"}
              </DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="grid grid-cols-4">
                <TabsTrigger value="details">Détails</TabsTrigger>
                <TabsTrigger value="steps">Étapes ({labForm.steps.length})</TabsTrigger>
                <TabsTrigger value="environment" className="gap-1"><Terminal className="h-3 w-3" /> Terminal</TabsTrigger>
                <TabsTrigger value="association">Association</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Titre</Label>
                  <Input value={labForm.title} onChange={(e) => setLabForm({...labForm, title: e.target.value})} placeholder="Docker Basics - Premier Container" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={labForm.description} onChange={(e) => setLabForm({...labForm, description: e.target.value})} rows={3} placeholder="Décrivez le contenu de ce lab..." />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select value={labForm.category} onValueChange={(v) => setLabForm({...labForm, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Difficulté</Label>
                    <Select value={labForm.difficulty} onValueChange={(v) => setLabForm({...labForm, difficulty: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DIFFICULTIES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Durée (min)</Label>
                    <Input type="number" value={labForm.duration_minutes} onChange={(e) => setLabForm({...labForm, duration_minutes: parseInt(e.target.value) || 30})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tags (séparés par des virgules)</Label>
                  <Input value={labForm.tags} onChange={(e) => setLabForm({...labForm, tags: e.target.value})} placeholder="docker, containers, beginner" />
                </div>
              </TabsContent>

              <TabsContent value="steps" className="py-4">
                <StepEditor steps={labForm.steps} setSteps={(s) => setLabForm({...labForm, steps: s})} labForm={labForm} />
              </TabsContent>

              <TabsContent value="environment" className="space-y-4 py-4">
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    Configurez l'environnement terminal du lab. Les candidats verront ces ressources pré-déployées quand ils exécutent des commandes comme <code className="bg-muted px-1 rounded">kubectl get pods</code> ou <code className="bg-muted px-1 rounded">docker ps</code>.
                  </p>
                </div>

                {/* Presets */}
                <div className="space-y-2">
                  <Label className="text-sm">Modèles prédéfinis</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Button variant="outline" size="sm" className="h-auto py-2 px-3 flex flex-col items-start gap-1" onClick={() => setLabForm({...labForm, terminal_environment: {
                      deployments: [{ name: "nginx-deployment", image: "nginx:latest", replicas: 1, ready: 1, port: "80/TCP", age: "15m" }],
                      pods: [{ name: "nginx-deployment-7d4f85ccb-x9k2p", image: "nginx:latest", ready: "1/1", status: "Running", restarts: "0", age: "15m" }],
                      services: [
                        { name: "kubernetes", type: "ClusterIP", cluster_ip: "10.96.0.1", ports: "443/TCP", age: "30d" },
                        { name: "nginx-service", type: "ClusterIP", cluster_ip: "10.96.45.12", ports: "80/TCP", age: "15m" }
                      ],
                      containers: [],
                      files: { "/root/deployment.yaml": "apiVersion: apps/v1\nkind: Deployment\nmetadata:\n  name: nginx-deployment\nspec:\n  replicas: 1\n  selector:\n    matchLabels:\n      app: nginx\n  template:\n    metadata:\n      labels:\n        app: nginx\n    spec:\n      containers:\n      - name: nginx\n        image: nginx:latest\n        ports:\n        - containerPort: 80" }
                    }})}>
                      <div className="flex items-center gap-1.5 text-xs font-medium text-blue-400">
                        <Server className="h-3 w-3" /> K8s — Nginx
                      </div>
                      <span className="text-[10px] text-muted-foreground text-left">Deployment nginx 1 replica + service</span>
                    </Button>
                    <Button variant="outline" size="sm" className="h-auto py-2 px-3 flex flex-col items-start gap-1" onClick={() => setLabForm({...labForm, terminal_environment: {
                      containers: [
                        { id: "a1b2c3d4e5f6", name: "web-nginx", image: "nginx:latest", ports: "0.0.0.0:8080->80/tcp", status: "Up 10 minutes", created: "10 minutes ago" },
                        { id: "f6e5d4c3b2a1", name: "app-redis", image: "redis:alpine", ports: "6379/tcp", status: "Up 10 minutes", created: "10 minutes ago" }
                      ],
                      pods: [], deployments: [],
                      services: [{ name: "kubernetes", type: "ClusterIP", cluster_ip: "10.96.0.1", ports: "443/TCP", age: "30d" }],
                      files: { "/root/docker-compose.yml": "version: '3.8'\nservices:\n  web:\n    image: nginx:latest\n    ports:\n      - '8080:80'\n  redis:\n    image: redis:alpine" }
                    }})}>
                      <div className="flex items-center gap-1.5 text-xs font-medium text-purple-400">
                        <Container className="h-3 w-3" /> Docker Multi
                      </div>
                      <span className="text-[10px] text-muted-foreground text-left">Nginx + Redis containers</span>
                    </Button>
                    <Button variant="outline" size="sm" className="h-auto py-2 px-3 flex flex-col items-start gap-1" onClick={() => setLabForm({...labForm, terminal_environment: {}})}>
                      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400">
                        <Terminal className="h-3 w-3" /> Vide
                      </div>
                      <span className="text-[10px] text-muted-foreground text-left">Terminal vierge</span>
                    </Button>
                  </div>
                </div>

                {/* JSON Editor */}
                <div className="space-y-2">
                  <Label className="text-sm">Configuration JSON</Label>
                  <Textarea
                    value={JSON.stringify(labForm.terminal_environment || {}, null, 2)}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setLabForm({...labForm, terminal_environment: parsed});
                      } catch { /* ignore parse errors while typing */ }
                    }}
                    rows={12}
                    className="font-mono text-xs"
                    placeholder='{"containers": [], "pods": [], "deployments": [], "services": [], "files": {}}'
                  />
                  <p className="text-[10px] text-muted-foreground">
                    <strong>containers</strong>: Docker containers visibles avec <code>docker ps</code> · 
                    <strong> pods</strong>: Pods K8s visibles avec <code>kubectl get pods</code> · 
                    <strong> deployments</strong>: Deployments K8s (supportent <code>kubectl scale</code>) · 
                    <strong> services</strong>: Services K8s · 
                    <strong> files</strong>: Fichiers accessibles via <code>cat</code>
                  </p>
                </div>

                {/* Preview */}
                {Object.keys(labForm.terminal_environment || {}).length > 0 && (
                  <div className="p-3 bg-[#1e1e2e] rounded-lg font-mono text-xs text-[#cdd6f4] space-y-2">
                    <p className="text-[#6c7086]">Aperçu de l'environnement :</p>
                    {(labForm.terminal_environment?.containers || []).length > 0 && (
                      <div>
                        <span className="text-[#89b4fa]">🐳 Containers:</span>{" "}
                        {labForm.terminal_environment.containers.map(c => c.name).join(", ")}
                      </div>
                    )}
                    {(labForm.terminal_environment?.pods || []).length > 0 && (
                      <div>
                        <span className="text-[#a6e3a1]">☸️ Pods:</span>{" "}
                        {labForm.terminal_environment.pods.map(p => p.name).join(", ")}
                      </div>
                    )}
                    {(labForm.terminal_environment?.deployments || []).length > 0 && (
                      <div>
                        <span className="text-[#f9e2af]">📦 Deployments:</span>{" "}
                        {labForm.terminal_environment.deployments.map(d => `${d.name} (${d.replicas} replicas)`).join(", ")}
                      </div>
                    )}
                    {(labForm.terminal_environment?.services || []).filter(s => s.name !== "kubernetes").length > 0 && (
                      <div>
                        <span className="text-[#cba6f7]">🔗 Services:</span>{" "}
                        {labForm.terminal_environment.services.filter(s => s.name !== "kubernetes").map(s => `${s.name} (${s.type})`).join(", ")}
                      </div>
                    )}
                    {Object.keys(labForm.terminal_environment?.files || {}).length > 0 && (
                      <div>
                        <span className="text-[#fab387]">📄 Fichiers:</span>{" "}
                        {Object.keys(labForm.terminal_environment.files).join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="association" className="space-y-4 py-4">
                <div className="p-4 bg-muted/30 rounded-lg border border-border space-y-4">
                  <p className="text-sm text-muted-foreground">Associez ce lab à un workspace et/ou un cours pour qu'il apparaisse dans le sidebar des candidats.</p>
                  <div className="space-y-2">
                    <Label>Workspace</Label>
                    <Select value={labForm.workspace_id || "none"} onValueChange={(v) => { const wid = v === "none" ? "" : v; setLabForm({...labForm, workspace_id: wid, course_id: "", category_id: ""}); loadCourses(wid); loadWsCategories(wid); }}>
                      <SelectTrigger><SelectValue placeholder="Aucun workspace" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun workspace</SelectItem>
                        {workspaces.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {labForm.workspace_id && (
                    <div className="space-y-2">
                      <Label>Cours</Label>
                      <Select value={labForm.course_id || "none"} onValueChange={(v) => setLabForm({...labForm, course_id: v === "none" ? "" : v})}>
                        <SelectTrigger><SelectValue placeholder="Aucun cours" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun cours (lab indépendant)</SelectItem>
                          {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {labForm.workspace_id && wsCategories.length > 0 && (
                    <div className="space-y-2">
                      <Label>Catégorie</Label>
                      <Select value={labForm.category_id || "none"} onValueChange={(v) => setLabForm({...labForm, category_id: v === "none" ? "" : v})}>
                        <SelectTrigger><SelectValue placeholder="Aucune catégorie" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucune catégorie</SelectItem>
                          {wsCategories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded" style={{ backgroundColor: cat.color }} />
                                {cat.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Annuler</Button>
              <Button onClick={handleSave} className="bg-gradient-to-r from-cyan-600 to-blue-600">
                {editingLab ? "Mettre à jour" : "Créer le Lab"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg"><FlaskConical className="h-5 w-5 text-blue-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.total_labs || 0}</p>
              <p className="text-xs text-muted-foreground">Labs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg"><Users className="h-5 w-5 text-purple-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.total_sessions || 0}</p>
              <p className="text-xs text-muted-foreground">Sessions</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg"><CheckCircle className="h-5 w-5 text-green-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.completed_sessions || 0}</p>
              <p className="text-xs text-muted-foreground">Complétés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg"><Trophy className="h-5 w-5 text-amber-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.completion_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Taux complétion</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Labs / Results */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="labs" className="gap-2"><BookOpen className="h-4 w-4" /> Labs</TabsTrigger>
          <TabsTrigger value="results" className="gap-2"><Trophy className="h-4 w-4" /> Résultats</TabsTrigger>
        </TabsList>

        {/* Labs List */}
        <TabsContent value="labs" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Rechercher un lab..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filteredLabs.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">Aucun lab trouvé. Créez votre premier lab !</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredLabs.map(lab => (
                <Card key={lab.id} className="hover:border-primary/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Terminal className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold truncate">{lab.title}</h3>
                          <p className="text-sm text-muted-foreground truncate">{lab.description}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getCategoryBadge(lab.category)}
                          {getDifficultyBadge(lab.difficulty)}
                          <Badge variant="outline" className="font-mono text-xs">
                            <Clock className="h-3 w-3 mr-1" />{lab.duration_minutes}min
                          </Badge>
                          <Badge variant="outline" className="font-mono text-xs">
                            {lab.steps?.length || 0} étapes
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 ml-4 text-xs text-muted-foreground">
                          <span>{lab.total_attempts || 0} tentatives</span>
                          <span>•</span>
                          <span>{lab.completions || 0} complétés</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(lab)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(lab.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Results */}
        <TabsContent value="results" className="space-y-4">
          {results.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">Aucun résultat disponible</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-3 font-medium">Candidat</th>
                        <th className="text-left p-3 font-medium">Lab</th>
                        <th className="text-center p-3 font-medium">Progression</th>
                        <th className="text-center p-3 font-medium">Score</th>
                        <th className="text-center p-3 font-medium">Statut</th>
                        <th className="text-right p-3 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map(r => {
                        const isExpanded = expandedResults.has(r.session_id);
                        return (
                        <React.Fragment key={r.session_id}>
                          <tr 
                            className="border-b border-border/50 hover:bg-muted/20 cursor-pointer transition-colors"
                            onClick={() => toggleResult(r.session_id)}
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                <div>
                                  <p className="font-medium">{r.user_name}</p>
                                  <p className="text-xs text-muted-foreground">{r.user_email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="font-medium">{r.lab_title}</span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${r.total_steps > 0 ? (r.completed_steps / r.total_steps * 100) : 0}%` }} />
                                </div>
                                <span className="text-xs font-mono">{r.completed_steps}/{r.total_steps}</span>
                              </div>
                            </td>
                            <td className="p-3 text-center font-mono font-bold text-violet-400">{r.score} pts</td>
                            <td className="p-3 text-center">
                              <Badge className={r.status === "completed" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                                {r.status === "completed" ? "Terminé" : "En cours"}
                              </Badge>
                            </td>
                            <td className="p-3 text-right text-xs text-muted-foreground">
                              {r.started_at ? new Date(r.started_at).toLocaleDateString("fr-FR", {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute:'2-digit'}) : "-"}
                            </td>
                          </tr>
                          {isExpanded && r.step_results && Object.keys(r.step_results).length > 0 && (
                            <tr className="bg-muted/5 border-b border-border">
                              <td colSpan={6} className="p-0">
                                <div className="p-4 pl-12">
                                  <div className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Détail des étapes</div>
                                  <div className="space-y-2">
                                    {Object.entries(r.step_results).map(([stepIdx, step]) => (
                                      <div key={stepIdx} className="flex items-start gap-3 p-2 rounded-lg bg-card border border-border/50">
                                        <div className="mt-0.5">
                                          {step.correct ? (
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                          ) : (
                                            <X className="h-4 w-4 text-red-500" />
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">{step.title}</span>
                                            <span className={`text-xs font-mono ${step.points > 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                                              +{step.points} pts
                                            </span>
                                          </div>
                                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                                            <Badge variant="outline" className="text-[10px] uppercase font-mono">{step.type}</Badge>
                                            {step.type === "quiz" && step.answer !== null && (
                                              <span>A répondu : Option {parseInt(step.answer) + 1}</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
